import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldExclamationIcon,
    PlusIcon,
    CalendarDaysIcon,
    CheckIcon,
    ClockIcon,
    ExclamationCircleIcon,
    BookmarkSquareIcon,
    ChatBubbleLeftEllipsisIcon,
    PencilSquareIcon,
    XMarkIcon,
    ArrowTopRightOnSquareIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ShieldCheckIcon,
    DocumentTextIcon,
    FingerPrintIcon,
} from "@heroicons/react/24/outline";
import { CloudArrowUpIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Button } from "../../ui";
import { toast } from "../../../utils/notify";
import Pagination from "../../ui/pagination";
import {
    createRiskTreatmentPlanApi,
    updateCustomerRiskApi,
    updateRiskTreatmentPlanApi,
    deleteRiskTreatmentPlanApi,
    fetchTreatmentFileApi,
    fetchEvaluationFileApi,
    getRiskTreatmentListApi
} from "../../../services/axios/AuthApis/RisksManagementApis";
import { RISK_TREATMENT_STATUS, TREATMENT_TYPES } from "../../../constants/AppEnums";
import { useSelector } from "react-redux";
import ApiLoader from "../../ui/ApiLoader";
import { TreatmentFileUpload } from "./TreatmentFileUpload";

const inputClass = (hasError = false, hasIcon = true) =>
    `w-full ${hasIcon ? "pl-9" : "pl-3"} pr-3 py-2.5 border ${hasError ? "border-red-400" : "border-gray-200"} rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white`;

// Fully independent of Risk Register/Analysis/Evaluation - own state,
// own fetch function, own backend endpoint (getRiskTreatmentListApi /
// get_risk_treatment_list), same isolation reasoning as Risk Analysis.
export const RiskTreatment = () => {
    const userDetail = useSelector((state) => state.user);
    const selectedCompany = useSelector((state) => state.company.selectedCompany);
    const userRole = userDetail?.user?.role;
    const customerId = (userRole === 'security_analyst' || userRole === 'auditor')
        ? selectedCompany?.user_id
        : userDetail?.user?.id;

    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTreatmentModal, setShowTreatmentModal] = useState(false);
    const [selectedRiskId, setSelectedRiskId] = useState(null);
    const [selectedRisk, setSelectedRisk] = useState(null);
    const [selectedTreatment, setSelectedTreatment] = useState(null);
    const [remarks, setRemarks] = useState({});
    const [saving, setSaving] = useState({});
    const [expandedRisk, setExpandedRisk] = useState(null);
    const [showHistory, setShowHistory] = useState({});
    const [showUploadFile, setShowUploadFile] = useState(false);
    const [totalRiskCount, setTotalRiskCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const requestIdRef = useRef(0);

    const totalPages = Math.max(1, Math.ceil(totalRiskCount / itemsPerPage));

    const fetchData = async (page = currentPage) => {
        if (!customerId) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const response = await getRiskTreatmentListApi(customerId, page, itemsPerPage);
            if (requestId !== requestIdRef.current) return;
            if (response.success) {
                setRisks(response.data || []);
                setTotalRiskCount(response.total_count || 0);
                setCurrentPage(page);
            }
        } catch (err) {
            console.error("Error loading risks:", err);
            toast.error("Failed to load risks");
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
    }, [customerId, itemsPerPage]);

    const handlePageChange = (page, pageSize) => {
        if (pageSize !== undefined && pageSize !== itemsPerPage) {
            setItemsPerPage(pageSize);
            setCurrentPage(1);
            return;
        }
        if (page >= 1 && page <= totalPages) {
            fetchData(page);
        }
    };

    const openTreatmentModal = (risk, treatment = null) => {
        setSelectedRiskId(risk.id);
        setSelectedRisk(risk);
        setSelectedTreatment(treatment);
        setShowTreatmentModal(true);
    };

    const handleTreatmentSuccess = () => {
        toast.success(selectedTreatment ? "Treatment updated successfully" : "Treatment created successfully");
        setShowTreatmentModal(false);
        setSelectedRiskId(null);
        setSelectedRisk(null);
        setSelectedTreatment(null);
        fetchData(currentPage);
    };

    const updateTreatmentStatus = async (treatmentId, status) => {
        try {
            const response = await updateRiskTreatmentPlanApi({ status }, treatmentId);
            if (response.success) {
                setRisks(prevRisks => prevRisks.map(risk => ({
                    ...risk,
                    risk_treatment_list: (risk.risk_treatment_list || []).map(t =>
                        t.id === treatmentId ? { ...t, status } : t
                    )
                })));
                toast.success(`Treatment status updated to ${status}`);
                fetchData(currentPage);
            }
        } catch (err) {
            console.error("Failed to update status:", err);
            toast.error("Failed to update status");
        }
    };

    const saveRemarks = async (riskId) => {
        const risk = risks.find(r => r.id === riskId);
        if (!risk) return;
        setSaving(prev => ({ ...prev, [`remarks_${riskId}`]: true }));
        try {
            const updateData = { ...risk, treatment_note: remarks[riskId] || "" };
            const response = await updateCustomerRiskApi(updateData, risk.id);
            if (response.success) {
                setRisks(updateData => risks.map(r => r.id === riskId ? { ...r, ...updateData } : r));
                toast.success(`Remarks saved for ${risk.risk_id}`);
                fetchData(currentPage);
            }
        } catch (err) {
            toast.error("Failed to save remarks");
            console.error("Failed to save remarks:", err);
        } finally {
            setSaving(prev => ({ ...prev, [`remarks_${riskId}`]: false }));
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "Implemented": return <CheckIcon className="w-4 h-4 text-green-600" />;
            case "In Progress": return <ClockIcon className="w-4 h-4 text-amber-600" />;
            default: return <ExclamationCircleIcon className="w-4 h-4 text-gray-400" />;
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case "Implemented": return "bg-green-100 text-green-700 border-green-200";
            case "In Progress": return "bg-amber-100 text-amber-700 border-amber-200";
            default: return "bg-gray-100 text-gray-700 border-gray-200";
        }
    };

    const toggleHistory = (riskId) => {
        setShowHistory(prev => ({ ...prev, [riskId]: !prev[riskId] }));
    };

    const handleTreatmentFileView = async (treatment) => {
        try {
            const res = await fetchTreatmentFileApi(treatment.id);
            if (res) window.open(res.data.data, "_blank", "noopener,noreferrer");
        } catch (err) {
            console.error("Error", err);
        }
    };

    const handleEvaluationFileView = async (riskId) => {
        if (!riskId) return;
        try {
            const res = await fetchEvaluationFileApi(riskId);
            if (res) window.open(res.data.data, "_blank", "noopener,noreferrer");
        } catch (err) {
            console.error("Error", err);
        }
    };

    if (loading && risks.length === 0) {
        return <ApiLoader label="Loading Treatment Plan…" className="py-20" />;
    }

    return (
        <div className="space-y-6" data-testid="risk-treatment-tab">

            {/* Treatment Types Info Card */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                    <ShieldExclamationIcon className="w-5 h-5 text-primary-500" />
                    <h3 className="text-sm font-semibold text-gray-700">Treatment Types</h3>
                </div>
                <div className="px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-3 rounded-lg bg-primary-50 border border-primary-200">
                            <p className="font-semibold text-primary-700 text-sm">Preventive</p>
                            <p className="text-xs text-primary-600 mt-1">Controls that prevent risk events</p>
                        </div>
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                            <p className="font-semibold text-amber-700 text-sm">Detective</p>
                            <p className="text-xs text-amber-600 mt-1">Controls that detect risk events</p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                            <p className="font-semibold text-green-700 text-sm">Corrective</p>
                            <p className="text-xs text-green-600 mt-1">Controls that correct impact</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Risk Cards */}
            <div className="space-y-4">
                {risks.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="py-14 text-center">
                            <ShieldExclamationIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-sm">No risks requiring treatment</p>
                            <p className="text-xs text-gray-400 mt-1">Risks with "Reduce" response will appear here</p>
                        </div>
                    </div>
                ) : (
                    risks.map(risk => {
                        const treatment = risk.has_treatments ? risk.risk_treatment_list : null;
                        const treatmentLogs = risk.treatment_log || [];
                        const groupedLogs = risk.grouped_logs || {};

                        return (
                            <div
                                key={risk.id}
                                className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                                data-testid={`treatment-risk-${risk.risk_id}`}
                            >
                                {/* Risk Card Header */}
                                <div className="px-6 py-4 border-b border-gray-100">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="text-sm font-mono font-semibold text-primary-600 flex-shrink-0">
                                                {risk.risk_id}
                                            </span>
                                            {risk.risk_category && (
                                                <span className="text-xs text-gray-400 truncate">
                                                    {risk.risk_category}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {risk.risk_response === "Accept" ? (
                                                <>
                                                    <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                                                        Risk Accepted
                                                    </span>
                                                    {risk.has_evaluation_file && (
                                                        <button
                                                            onClick={() => handleEvaluationFileView(risk.id)}
                                                            title="View evaluation file"
                                                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                        >
                                                            <EyeIcon className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                userRole !== "auditor" && (
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        onClick={() => openTreatmentModal(risk, treatment)}
                                                        data-testid={`${treatment ? 'edit' : 'add'}-treatment-${risk.risk_id}`}
                                                    >
                                                        {treatment ? (
                                                            <>
                                                                <PencilSquareIcon className="w-4 h-4 mr-1" />
                                                                Edit Treatment
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PlusIcon className="w-4 h-4 mr-1" />
                                                                Add Treatment
                                                            </>
                                                        )}
                                                    </Button>
                                                )
                                            )}
                                            {risk.risk_response !== "Accept" && treatment && userRole !== "auditor" && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedTreatment(treatment);
                                                        setShowUploadFile(true);
                                                    }}
                                                    title="Upload file"
                                                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                >
                                                    <CloudArrowUpIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                            {treatment && treatment.has_treatment_file && (
                                                <button
                                                    onClick={() => handleTreatmentFileView(treatment)}
                                                    title="View treatment file"
                                                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                >
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">{risk.risk_description}</p>
                                </div>

                                {/* Risk Card Body */}
                                <div className="px-6 py-4 space-y-3">
                                    {!treatment ? (
                                        <p className="text-sm text-gray-400 py-4 text-center">No treatment plans yet</p>
                                    ) : (
                                        <div className="space-y-3">
                                            {/* Treatment Detail */}
                                            <div
                                                className="p-4 rounded-xl bg-gray-50 border border-gray-200"
                                                data-testid={`treatment-${treatment.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                treatment.treatment_type === "Preventive" ? "bg-primary-100 text-primary-700" :
                                                                treatment.treatment_type === "Detective" ? "bg-amber-100 text-amber-700" :
                                                                "bg-green-100 text-green-700"
                                                            }`}>
                                                                {treatment.treatment_type}
                                                            </span>
                                                            <span className="text-xs text-gray-500 font-mono">{treatment.control_reference}</span>
                                                        </div>
                                                        {treatment.description && (
                                                            <p className="text-sm text-gray-600 mb-2 leading-relaxed">{treatment.description}</p>
                                                        )}
                                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                                            <span className="flex items-center gap-1">
                                                                <CalendarDaysIcon className="w-3.5 h-3.5" />
                                                                Target: {treatment.target_date || "Not set"}
                                                            </span>
                                                            {treatment.linked_modules && treatment.linked_modules.length > 0 && (
                                                                <span className="flex items-center gap-1">
                                                                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                                    {treatment.linked_modules.length} linked module(s)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <div className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 ${getStatusClass(treatment.status)}`}>
                                                            {getStatusIcon(treatment.status)}
                                                            <select
                                                                value={treatment.status || ""}
                                                                onChange={(e) => updateTreatmentStatus(treatment.id, e.target.value)}
                                                                className="text-xs font-medium bg-transparent border-none focus:outline-none cursor-pointer"
                                                            >
                                                                {RISK_TREATMENT_STATUS.map(s => (
                                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Treatment History */}
                                            {treatmentLogs.length > 0 && (
                                                <div className="rounded-xl bg-gray-50 border border-gray-200 overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleHistory(risk.id)}
                                                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors text-left"
                                                    >
                                                        <ClockIcon className="w-4 h-4" />
                                                        <span className="flex-1 font-medium">
                                                            {showHistory[risk.id] ? 'Hide History' : 'Show History'}
                                                        </span>
                                                        {showHistory[risk.id]
                                                            ? <ChevronUpIcon className="w-4 h-4" />
                                                            : <ChevronDownIcon className="w-4 h-4" />
                                                        }
                                                    </button>
                                                    {showHistory[risk.id] && (
                                                        <div className="px-4 pb-4 pt-2">
                                                            <div className="pl-4 border-l-2 border-gray-300 ml-2 space-y-4">
                                                                {Object.entries(groupedLogs).map(([date, logs]) => (
                                                                    <div key={date}>
                                                                        <div className="flex items-center mb-2">
                                                                            <div className="w-2.5 h-2.5 rounded-full bg-primary-500 mr-2 flex-shrink-0" />
                                                                            <span className="text-xs font-semibold text-gray-700">{date}</span>
                                                                        </div>
                                                                        <div className="space-y-2 ml-4">
                                                                            {logs.map((log, index) => (
                                                                                <div key={index} className="relative pb-1">
                                                                                    <div className="absolute -left-6 top-2 w-2.5 h-2.5 rounded-full border-2 border-white bg-gray-300 z-10" />
                                                                                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                                                                                        <div className="flex items-center gap-2 mb-1">
                                                                                            {log.type === 'created' && <PlusIcon className="w-4 h-4 text-green-600 flex-shrink-0" />}
                                                                                            {log.type === 'status' && <ExclamationCircleIcon className="w-4 h-4 text-primary-600 flex-shrink-0" />}
                                                                                            {log.type === 'treatment_type' && <ShieldCheckIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />}
                                                                                            {log.type === 'date' && <CalendarDaysIcon className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                                                                                            <span className="text-sm font-medium text-gray-800">{log.text}</span>
                                                                                        </div>
                                                                                        <p className="text-xs text-gray-500">by {log.logged_by || 'system'}</p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Treatment Remarks */}
                                    <div className="pt-3 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => setExpandedRisk(expandedRisk === risk.id ? null : risk.id)}
                                            className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 transition-colors w-full text-left"
                                        >
                                            <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                                            <span className="font-medium">
                                                {expandedRisk === risk.id ? "Hide Remarks" : "Add / View Treatment Remarks"}
                                            </span>
                                            {expandedRisk === risk.id
                                                ? <ChevronUpIcon className="w-4 h-4 ml-auto" />
                                                : <ChevronDownIcon className="w-4 h-4 ml-auto" />
                                            }
                                        </button>

                                        {expandedRisk === risk.id && (
                                            <div className="mt-3 space-y-2">
                                                <textarea
                                                    value={remarks[risk.id] ?? risk.treatment_note ?? ""}
                                                    onChange={(e) => setRemarks(prev => ({ ...prev, [risk.id]: e.target.value }))}
                                                    placeholder="Add treatment notes, progress updates, implementation details…"
                                                    rows={3}
                                                    data-testid={`treatment-remarks-${risk.risk_id}`}
                                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white resize-none"
                                                />
                                                <div className="flex justify-end">
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        onClick={() => saveRemarks(risk.id)}
                                                        disabled={saving[`remarks_${risk.id}`]}
                                                        loading={saving[`remarks_${risk.id}`]}
                                                    >
                                                        <BookmarkSquareIcon className="w-4 h-4 mr-1" />
                                                        {saving[`remarks_${risk.id}`] ? "Saving…" : "Save Remarks"}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}

                {/* Pagination */}
                {totalRiskCount > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalRiskCount}
                        pageSize={itemsPerPage}
                        onPageChange={(newPage, newPageSize) => handlePageChange(newPage, newPageSize)}
                    />
                )}
            </div>

            {/* Treatment Modal */}
            <TreatmentModal
                isOpen={showTreatmentModal}
                onClose={() => {
                    setShowTreatmentModal(false);
                    setSelectedRiskId(null);
                    setSelectedRisk(null);
                    setSelectedTreatment(null);
                }}
                risk={selectedRisk}
                treatment={selectedTreatment}
                userDetail={userDetail}
                onSuccess={handleTreatmentSuccess}
            />

            <TreatmentFileUpload
                showUploadFile={showUploadFile}
                setShowUploadFile={setShowUploadFile}
                selectedTreatment={selectedTreatment}
            />
        </div>
    );
};

const TreatmentModal = ({ isOpen, onClose, risk, treatment, onSuccess, userDetail }) => {
    const [formData, setFormData] = useState({
        treatment_type: "",
        control_reference: "",
        linked_modules: [],
        target_date: "",
        description: "",
        status: "Pending"
    });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (treatment) {
                setFormData({
                    treatment_type: treatment.treatment_type || "",
                    control_reference: treatment.control_reference || "",
                    linked_modules: treatment.linked_modules || [],
                    target_date: treatment.target_date || "",
                    description: treatment.description || "",
                    status: treatment.status || "Pending"
                });
            } else {
                setFormData({
                    treatment_type: "",
                    control_reference: "",
                    linked_modules: [],
                    target_date: "",
                    description: "",
                    status: "Pending"
                });
            }
            setErrors({});
        }
    }, [isOpen, treatment]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.treatment_type) newErrors.treatment_type = "Treatment type is required";
        if (!formData.control_reference) newErrors.control_reference = "Control reference is required";
        if (!formData.target_date) {
            newErrors.target_date = "Target date is required";
        } else {
            const selected = new Date(formData.target_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selected < today) newErrors.target_date = "Target date cannot be in the past";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Please fix the errors before submitting");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                treatment_type: formData.treatment_type,
                control_reference: formData.control_reference,
                description: formData.description,
                target_date: formData.target_date,
                employee_id: userDetail?.user?.id
            };
            if (treatment) payload.status = formData.status;

            const response = treatment
                ? await updateRiskTreatmentPlanApi(payload, treatment.id)
                : await createRiskTreatmentPlanApi(risk.id, payload);

            if (response.success) {
                toast.success(treatment ? "Treatment updated successfully" : "Treatment created successfully");
                onSuccess();
            } else {
                toast.error(response.message || "Failed to save treatment");
            }
        } catch (err) {
            console.error("Error saving treatment:", err);
            toast.error("Failed to save treatment");
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Modal Panel */}
                    <motion.div
                        key="modal"
                        initial={{ opacity: 0, y: -24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.97 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh] overflow-hidden"
                        data-testid="treatment-modal"
                    >
                        {/* Header */}
                        <div className="flex items-center gap-4 px-6 py-5 bg-gradient-to-r from-primary-600 to-primary-500 rounded-t-2xl flex-shrink-0">
                            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                <ShieldExclamationIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-white leading-tight">
                                    {treatment ? "Edit Treatment Plan" : "Create Treatment Plan"}
                                </h3>
                                {risk && (
                                    <p className="text-xs text-primary-100 mt-0.5 truncate">
                                        <span className="font-mono font-semibold">{risk.risk_id}</span>
                                        {risk.risk_description && ` — ${risk.risk_description}`}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <form onSubmit={handleSubmit} id="treatment-form" className="flex-1 overflow-y-auto">
                            <div className="px-6 py-5 space-y-5">

                                {/* Treatment Type */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Treatment Type <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <ShieldCheckIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                                        <select
                                            value={formData.treatment_type}
                                            onChange={(e) => handleChange("treatment_type", e.target.value)}
                                            className={inputClass(!!errors.treatment_type)}
                                        >
                                            <option value="">Select treatment type…</option>
                                            {TREATMENT_TYPES.map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.treatment_type && <p className="mt-1 text-xs text-red-500">{errors.treatment_type}</p>}
                                </div>

                                {/* Control Reference */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Control Reference <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <FingerPrintIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                                        <input
                                            type="text"
                                            value={formData.control_reference}
                                            onChange={(e) => handleChange("control_reference", e.target.value)}
                                            placeholder="e.g. AC-001, VM-002"
                                            className={inputClass(!!errors.control_reference)}
                                        />
                                    </div>
                                    {errors.control_reference && <p className="mt-1 text-xs text-red-500">{errors.control_reference}</p>}
                                </div>

                                {/* Target Date */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Target Date <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <CalendarDaysIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                                        <input
                                            type="date"
                                            value={formData.target_date}
                                            onChange={(e) => handleChange("target_date", e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                            className={inputClass(!!errors.target_date)}
                                        />
                                    </div>
                                    {errors.target_date && <p className="mt-1 text-xs text-red-500">{errors.target_date}</p>}
                                </div>

                                {/* Status (edit mode only) */}
                                {treatment && (
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                            Status
                                        </label>
                                        <div className="relative">
                                            <CheckIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                                            <select
                                                value={formData.status}
                                                onChange={(e) => handleChange("status", e.target.value)}
                                                className={inputClass(false)}
                                            >
                                                {RISK_TREATMENT_STATUS.map(s => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Description */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Description
                                    </label>
                                    <div className="relative">
                                        <DocumentTextIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleChange("description", e.target.value)}
                                            placeholder="Describe the treatment plan, implementation steps, responsibilities…"
                                            rows={4}
                                            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none bg-gray-50 focus:bg-white"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Provide details about the treatment implementation</p>
                                </div>

                            </div>
                        </form>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl flex-shrink-0">
                            <p className="text-xs text-gray-400">
                                <span className="text-red-400">*</span> Required fields
                            </p>
                            <div className="flex items-center gap-3">
                                <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    form="treatment-form"
                                    variant="primary"
                                    size="sm"
                                    disabled={saving}
                                    loading={saving}
                                >
                                    {saving
                                        ? (treatment ? "Updating…" : "Creating…")
                                        : (treatment ? "Update Treatment" : "Create Treatment")
                                    }
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
